import React, { useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useSession } from '@slerp/accounts'
import {
  QUERY_MERCHANT_INVOICES,
  QUERY_STORE_INVOICES
} from '../SettingsQueries'
import { Table, Tag, Button, Row, Col, Result } from 'antd'
import {
  InvoiceStatus,
  Invoice,
  SelectFilterVariant,
  Pagination,
  InvoiceKind
} from './types'
import {
  getStatusColor,
  getBillingPeriod,
  getInvoiceParams,
  formatDate
} from './utils'
import { Store } from 'types'
import styled from '@emotion/styled'
import { DownloadIcon } from 'packages/@slerp/assets'
import SelectFilter from './InvoiceFilter'
import { formatMoney } from 'packages/@slerp/designer-rework/shop/components/Cart/utils'
import { SorterTitle, SortDirectionOptions } from '../../Common'

interface Props {
  store?: Store
  kind: InvoiceKind
}

const Invoices = ({ store, kind }: Props) => {
  const { merchant, user } = useSession()

  const [invoices, setInvoices] = React.useState<Invoice[]>([])
  const [pagination, setPagination] = React.useState<Pagination | null>()
  const [selectedStatus, setSelectedStatus] = React.useState<string[]>([])
  const [sortDirection, setSortDirection] =
    React.useState<SortDirectionOptions>(SortDirectionOptions.descend)

  const query = Boolean(store) ? QUERY_STORE_INVOICES : QUERY_MERCHANT_INVOICES

  const [fetchInvoices, { loading }] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const invoiceResult = Boolean(store)
        ? data.listStoreInvoices.invoices
        : data.listInvoices.invoices
      setInvoices(invoiceResult)

      if (invoiceResult.length < 10) {
        setPagination(null)
        return
      }

      setPagination(
        Boolean(store)
          ? data.listStoreInvoices.pagination
          : data.listInvoices.pagination
      )
    }
  })

  const [fetchMoreInvoices, { loading: loadingMore }] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const invoiceResult = Boolean(store)
        ? data.listStoreInvoices.invoices
        : data.listInvoices.invoices
      setInvoices((prev) =>
        [...prev, ...invoiceResult].filter((value) => !!value)
      )
      setPagination(
        Boolean(store)
          ? data.listStoreInvoices.pagination
          : data.listInvoices.pagination
      )
    }
  })

  useEffect(() => {
    fetchInvoices({
      variables: getInvoiceParams({
        storeId: store?.id || '',
        merchantId: merchant?.id || '',
        paymentStatus: selectedStatus,
        sortBy: 'ACCOUNTING_DATE',
        sortDirection,
        kind
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, sortDirection])

  const COLUMNS = [
    {
      title: kind === InvoiceKind.invoice ? 'Invoice #' : 'Receipt #',
      dataIndex: 'invoiceReference',
      key: 'invoiceReference',
      render: (reference: string) => <CellSpan>#{reference}</CellSpan>
    },
    {
      title: (
        <div style={{ textAlign: 'center' }}>
          <span>Status</span>
        </div>
      ),
      dataIndex: 'status',
      render: (status: InvoiceStatus) => (
        <Row justify='center' align='middle' style={{ marginBottom: 0 }}>
          <Col>
            <StatusTag text={getStatusColor(status)}>
              {status.toLowerCase()}
            </StatusTag>
          </Col>
        </Row>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (amount: string) => (
        <CellSpan>{formatMoney(parseFloat(amount))}</CellSpan>
      )
    },
    {
      title: 'Billing Period',
      render: (record: Invoice) => {
        return (
          <CellSpan>
            {getBillingPeriod(
              record.billingPeriodStart,
              record.billingPeriodEnd
            )}
          </CellSpan>
        )
      }
    },
    {
      title: (
        <SorterTitle
          onClick={() =>
            sortDirection === SortDirectionOptions.ascend
              ? setSortDirection(SortDirectionOptions.descend)
              : setSortDirection(SortDirectionOptions.ascend)
          }
          sortDirection={sortDirection}
        >
          Date
        </SorterTitle>
      ),
      dataIndex: 'date',
      render: (date: string) => <CellSpan>{formatDate(date)}</CellSpan>
    },
    {
      title: 'Due',
      dataIndex: 'due',
      render: (due: string) => <CellSpan>{formatDate(due)}</CellSpan>
    },
    {
      title: (
        <div style={{ textAlign: 'center' }}>
          <span>Action</span>
        </div>
      ),
      dataIndex: 'downloadLink',
      render: (downloadLink: string) => (
        <Row justify='center' align='middle' style={{ marginBottom: 0 }}>
          <Col>
            <a
              href={`${downloadLink}?token=${user?.api_key}`}
              target='_blank'
              rel='noreferrer'
            >
              <StyledButton>
                <DownloadIcon style={{ fontSize: '16px', marginTop: '2px' }} />
              </StyledButton>
            </a>
          </Col>
        </Row>
      )
    }
  ]

  const fetchNext = () => {
    if (!pagination?.after) {
      return
    }

    fetchMoreInvoices({
      variables: getInvoiceParams({
        storeId: store?.id || '',
        merchantId: merchant?.id || '',
        paymentStatus: selectedStatus,
        after: pagination.after,
        sortBy: 'ACCOUNTING_DATE',
        sortDirection,
        kind
      })
    })
  }

  const isEmptyWithNoFilters =
    invoices.length === 0 && selectedStatus.length === 0 && !loading

  if (isEmptyWithNoFilters) {
    return <Result status={404} subTitle='No invoices available' />
  }

  return (
    <>
      <Row>
        <Col>
          <SelectFilter
            setSelected={setSelectedStatus}
            selected={selectedStatus}
            variant={SelectFilterVariant.status}
          />
        </Col>
      </Row>
      <Table
        data-testid='campaigns-history-table'
        dataSource={invoices}
        columns={COLUMNS}
        pagination={false}
        loading={loading || loadingMore}
        rowKey='invoiceReference'
      />
      <Row style={{ marginTop: '10px' }} gutter={10} justify='end'>
        {pagination?.after && (
          <Col>
            <Button onClick={fetchNext}>Load more</Button>
          </Col>
        )}
      </Row>
    </>
  )
}

const CellSpan = styled.span`
  color: #4a4a68;
  font-size: 13px;
  font-weight: 500;
`

const StatusTag = styled(Tag)(
  ({ text }: { text: string }) => `
  background-color: #fff;
  color: ${text};
  border: 1px solid #DBD9DF;
  text-transform: capitalize;
`
)

const StyledButton = styled(Button)`
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

export default Invoices
